import {isExpectedError} from '@/src/shared/utils/expected-error'
import type {
  LoadPageDataParams,
  LoadPageDataUseCaseInterface,
  PageParamsOnFunctionsResolverInterface,
  PageParamsToSendResolverInterface,
} from '@/src/core/domain/page'
import type {SessionServiceInterface} from '../../domain/session'
import merge from 'lodash/merge'
import {PageData, ServerPageData} from '../../domain/entities/page/page-data'
import {Routes} from '@/src/config/routes'
import {inject, injectable} from 'inversify'
import {DI} from '@/src/config/di/types'
import {PageTransformFn} from '@/src/core/domain/page/functions/types'
import {PageFunctionsRegistry} from '@/src/core/domain/page/functions/registry'
import {isMobile, isWeb, uuid} from '@/src/shared/utils/functions'
import type {IApiClientService} from '@/src/ports/services/api'
import type {IInstrumentationService} from '@/src/ports/services/monitoring/instrumentation'
import {PageType} from '@/src/core/domain/entities/page'
import * as Sentry from '@sentry/nextjs'
import {toClientSession} from '@/src/core/domain/session/client-session'

@injectable()
export class LoadPageDataUseCase<T = any> implements LoadPageDataUseCaseInterface<T> {
  @inject(DI.IInstrumentationService)
  instrumentation!: IInstrumentationService

  @inject(DI.SessionServiceInterface)
  sessionService!: SessionServiceInterface
  @inject(DI.IApiClientService)
  apiService!: IApiClientService
  @inject(DI.PageParamsToSendResolverInterface)
  paramsToSendResolver!: PageParamsToSendResolverInterface
  @inject(DI.PageParamsOnFunctionsResolverInterface)
  paramsOnFunctionResolver!: PageParamsOnFunctionsResolverInterface

  @inject(DI.PageParamsFunctionsRegistry)
  pageFunctionsRegistry!: PageFunctionsRegistry

  async do(params: LoadPageDataParams, queryKey: any[]): Promise<PageData<T>> {
    return this.instrumentation.startSpanAsync(
      {
        name: `Загрузка данных страницы: ${params.type}`,
        attributes: {params},
      },
      async span => {
        const [session, authHeaders] = await this.instrumentation.startSpanAsync(
          {
            name: 'Получение сессии пользователя',
            attributes: {server: isWeb() && typeof window === 'undefined'},
          },
          () => this.sessionService.load(),
        )

        span.setAttribute('session_user_id', session?.user?.id ?? '-')

        params = await this.paramsOnFunctionResolver.do(params, session)

        const apiEndpoint = params.apiEndpoint ?? Routes.webApi.page.data

        span.setAttribute('ignore', params.ignore ?? false)

        span.setAttribute('suspendUntilClientReady', params.suspendUntilClientReady ?? false)

        if (params.extraParams && (params.ignore || params.suspendUntilClientReady)) {
          delete params.extraParams
        }

        const content = {} as T

        if (params.ignore) {
          return this.instrumentation.startSpan(
            {
              name: 'Игнорирование загрузки на сервере',
            },
            () => ({
              type: params.type,
              meta: null,
              content,
              fetchKey: uuid(),
              session: toClientSession(session),
              ignore: true,
              queryKey,
              ...params.static,
              breadcrumbs: (params.static?.breadcrumbs ?? []) as BreadcrumbItem[],
            }),
          )
        }

        if (params.suspendUntilClientReady) {
          return this.instrumentation.startSpan(
            {
              name: 'Игнорирование загрузки пока клиент не будет готов в получению данных',
            },
            () => ({
              type: params.type,
              meta: null,
              content,
              fetchKey: uuid(),
              session: toClientSession(session),
              suspendUntilClientReady: true,
              queryKey,
              ...params.static,
              breadcrumbs: (params.static?.breadcrumbs ?? []) as BreadcrumbItem[],
            }),
          )
        }

        const pageParameters = await this.paramsToSendResolver.do(params)
        let extraParams: Record<any, any> = {}

        if (params.extraParams) {
          extraParams = await params.extraParams()
          delete params.extraParams
        }

        const pageLoadParams = {...pageParameters, ...extraParams}

        let pageData: ServerPageData<T>

        try {
          const fetchData = (publicRequest: boolean) =>
            this.apiService.post<ServerPageData<T>>(apiEndpoint, {
              data: {
                type: params.type,
                parameters: pageLoadParams,
              },
              headers: publicRequest ? {} : authHeaders,
              ...(publicRequest ? {withoutSession: true} : {}),
            })
          if (params.revalidate && typeof window === 'undefined') {
            const {cachedFetch} = await import('@/src/shared/utils/server-cache')
            pageData = await cachedFetch(
              [apiEndpoint, params.type, JSON.stringify(pageLoadParams)],
              params.revalidate,
              () => fetchData(true),
            )
          } else {
            pageData = await fetchData(false)
          }
        } catch (e: unknown) {
          if (isExpectedError(e)) throw e
          // The API client reports the original error; do not create a second event.

          Sentry.logger.error('Загрузка данных страницы завершилась с ошибкой', {
            type: params.type,
            data: {
              parameters: pageLoadParams,
            },
            params,
            pageParameters,
            extraParams,
            authHeaders,
          })

          throw e
        }

        if (params.static) {
          pageData = merge(params.static, pageData)

          if (params.static.meta) {
            pageData = this.setMetaIfEmpty(pageData, params.static.meta ?? null)
          }
        }

        if (params.transformKey && pageData.type !== PageType.NotFound) {
          span.setAttribute('have_transform', true)
          try {
            const transformFn = this.pageFunctionsRegistry.get<PageTransformFn>(
              params.transformKey,
              'transform',
            )
            pageData = (await transformFn(pageData, session, isMobile())) as ServerPageData<T>
          } catch (e: unknown) {
            // TODO: заменить эксепшеном из PageFunctionsRegistry
            if (e instanceof Error && e.message.includes('not registered')) {
              /***/
            } else {
              throw e
            }
          }
        }

        return {
          ...pageData,
          session: toClientSession(
            typeof window === 'undefined' ? session : (await this.sessionService.load())[0],
          ),
          fetchKey: uuid(),
          queryKey,
          ignore: params.ignore,
          suspendUntilClientReady: params.suspendUntilClientReady,
        } satisfies PageData<T>
      },
    )
  }

  setMetaIfEmpty(pageData: ServerPageData, meta: Nullable<PageMeta>): ServerPageData {
    if (!pageData.meta) {
      pageData.meta = meta || {}
    } else {
      if (!pageData.meta.title && meta?.title) {
        pageData.meta.title = meta.title
      }
      if (!pageData.meta.description && meta?.description) {
        pageData.meta.description = meta.description
      }
      if (!pageData.meta.keywords && meta?.keywords) {
        pageData.meta.keywords = meta.keywords
      }
    }
    return pageData
  }
}
